// src/components/DocumentViewerComponent.js
// Updated: 11/10/2025 07:55 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Worker for react-pdf (using the stable v6 worker)
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

const ComponentLoadingSpinner = () => (
    <div className="flex items-center justify-center h-full">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-dashed border-blue-500"></div>
    </div>
);

/**
 * Reusable component to display a PDF or image document by its ID.
 */
export default function DocumentViewerComponent({ documentId }) {
    const [document, setDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [numPages, setNumPages] = useState(null);

    // 1. State and Ref for dynamic width management
    const [containerWidth, setContainerWidth] = useState(null);
    const containerRef = useRef(null);

    function onDocumentLoadSuccess({ numPages }) { setNumPages(numPages); }

    // Fetch the document details using the ID (no changes)
    useEffect(() => {
        if (!documentId) {
            setDocument(null);
            setIsLoading(false);
            return;
        }
        const fetchDocument = async () => {
            setIsLoading(true); setError('');
            try {
                const docRef = doc(db, 'documents', documentId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setDocument({ id: docSnap.id, ...docSnap.data() });
                } else {
                    setError('Linked document not found.');
                }
            } catch (err) {
                console.error('Error fetching document for viewer:', err);
                setError('Failed to load linked document.');
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchDocument();
    }, [documentId]);
    
    // 2. Effect to measure and update container width
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                // Set the width based on the parent container's clientWidth
                const width = containerRef.current.clientWidth;
                setContainerWidth(width > 0 ? width : null);
            }
        };

        // Initial call and attach resize listener
        updateWidth();
        window.addEventListener('resize', updateWidth);

        // Cleanup listener
        return () => window.removeEventListener('resize', updateWidth);
    }, [documentId]); // Re-run on document change to ensure width is set

    if (isLoading) return <ComponentLoadingSpinner />;
    if (error) return <p className="text-red-600">Error loading document.</p>;
    if (!document) return <p className="text-gray-500">No linked document found for this package.</p>;

    // Document rendering logic
    if (document.fileType === 'application/pdf') {
        return (
            // 3. Attach ref to the outermost container
            <div ref={containerRef}> 
                <Document
                    file={document.downloadURL}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={console.error}
                    loading={<ComponentLoadingSpinner />}
                >
                    {/* 4. Pass the calculated containerWidth to the Page component */}
                    {Array.from(new Array(numPages), (el, index) => (
                        <Page
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            className="mb-2 shadow-md"
                            width={containerWidth || undefined} // Use dynamic width
                        />
                    ))}
                    {numPages > 0 && <p className="text-center text-sm text-gray-500">Scroll to view {numPages} pages.</p>}
                </Document>
            </div>
        );
    }

    if (document.fileType === 'image/jpeg' || document.fileType === 'image/png') {
        return <img src={document.downloadURL} alt={document.fileName} className="w-full object-contain" />;
    }

    return <p>Unsupported file type: {document.fileType}</p>;
}